/**
 * 小助理形象定义
 * 悬浮按钮 / VN 对话框头像共用。
 * 头像 = 捏脸拼装渲染（ASSISTANT_AVATAR，与随机 NPC 同管线、同画风演进）。
 *
 * 【人设与口吻原则（2026-10-25 茄子定调，新台词先读）】
 * 夏夏，20 多岁的女孩，主角的同事也是朋友，相处融洽——**亲切但有职业分寸**：
 * 把事说清楚，可以带一点真实的情绪；称呼直呼主角昵称（不带「姐」等后缀，见 AssistantSystem.term）。
 * ⛔ 销售话术（「咱们」「好消息」「签了它」「自己过目」「不亏」——替玩家算账/假熟/煽动）；
 * ⛔ 打鸡血（「继续加油」「保持曝光很重要，记得发动态！」——口号式说教）；
 * ⛔ 占位符左右自加空格（拼接处「昵称，正文」紧贴，空格只在文案自身需要时由文案携带）。
 */
import { AvatarConfig } from '../avatar/config';
import { avatarPngSrc } from '../avatar/gamemount';

export const ASSISTANT = {
    id: 'assistant',
    /**
     * 名字（2026-10-10 常量化：全项目称呼统一读本字段——**命名定稿后只改这一行**，人脉卡/剧情文案/播报全部生效）。
     * 2026-10-25 茄子定名「夏夏」。剧情文案（FixedEvents 源文档）里写「小助理：」代称的，渲染时读本字段。
     */
    name: '夏夏',
    /**
     * 功能菜单那句开场白（台词表 #11：行首无 ~ 记号＝不拼昵称，原样出）。
     * 73 批起悬浮入口不再搭闲话，点开必出「查看实绩／网友评价／业内资讯」菜单 ⇒ 本句是菜单的引子。
     */
    menuHint: '怎么了？',
    /**
     * 「业内资讯」的默认回应（2026-10-06 用户令：功能先⛔ 实装，点了让夏夏回一句）。
     * 走菜单选项的现成 `choice.reply`（原位连播，⛔ 收起重弹），所以这句是**当面说的话**、⛔ 顶部气泡。
     * 本文件是用户编辑区：以后业内资讯真上线时，这条连同 `menuHint` 一起退役/改写。
     */
    newsReply: '近期没有业内资讯。',
    /**
     * Lv.1 点「网友评价」的答复（2026-10-06 用户令「Lv1 直接不显示网友评价」）：
     * 面板⛔ 开，夏夏原位回这一句。句首 `~` 记号＝句首拼昵称（台词表 2026-10-28 令），
     * `AssistPanel` 现拼时经 `joinTerm()` 统一处理。
     */
    noReviewsReply: '~现在搜你还什么都没有呢，继续加油吧。',
    /**
     * 开局 intro（2026-10-25 新手引导批；同日茄子多改：二、三句删＋口吻定调——只留身份一句；
     * 指路人脉页由人脉页红点自己完成）。文案源=docs/新手引导-设计方案.md（2026-10-28 茄子令：
     * intro 归新手引导文档管，台词表已删 #1 条目）——改文案先改文档再落这里，接线不动。
     */
    introLines: [
        '{player}你好~我是夏夏，以后我就是你的助理了。我在右下角，有事随时叫我。',
    ] as readonly string[],
} as const;

/** 夏夏捏脸定稿配置（2026-09-30 捏脸工坊导出）。改形象 = 改这里的数字，全游戏自动同步 */
export const ASSISTANT_AVATAR: AvatarConfig = {
    gender: 'f',
    face: 1,      // 圆脸
    skin: 1,
    hairBack: 3,  // 马尾
    hairSides: 0, // 无刘海（联动）
    hairFront: 1, // 齐刘海
    hair: 0,
    hairColor: 2, // 棕发
    brow: 0,      // 英气眉
    eye: 1,       // 圆眼
    eyeColor: 0,  // 深棕瞳
    nose: 0,
    noseDy: 0,
    mouth: 3,     // 笑
    mouthDy: 0,
    beard: 0,
    ageLines: 0,
    cloth: 1,     // 红帽衫
    glasses: 1,   // 方框眼镜
    acc: 0,
};

/** 头像 PNG src（捏脸渲染 + 全局缓存；VN 对话框/FAB 等场景共用，精灵未就绪时首次调用稍慢） */
export function assistantAvatarPngSrc(): Promise<string> {
    return avatarPngSrc(ASSISTANT_AVATAR);
}
